import { useState, useCallback } from 'react';
// Note: Using raw HTML table elements instead of shadcn Table
// because shadcn Table has nested overflow-auto div that breaks sticky header and dual scroll
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Skeleton } from '@/core/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { Edit2, Trash2, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Check, X, Loader2, Info, ChevronUp, ChevronDown } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/core/components/ui/tooltip';
import { TranslationKey, Language } from '../../types/translation-types';
import { cn } from '@/core/lib/utils';
import { GRID_STYLES, EMPTY_CELL_VALUE } from '@/core/components/ui/grid-styles';

// Helper function to format date for display
function formatDateTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
}

interface TranslationGridProps {
  translations: TranslationKey[];
  languages: Language[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onEdit: (translation: TranslationKey) => void;
  onDelete: (translationId: number) => void;
  onUpdateCell: (translationId: number, languageCode: string, value: string) => Promise<void>;
  onUpdateDescription: (translationId: number, description: string) => Promise<void>;
  onRetry: () => void;
  isDeleting?: boolean;
  isSearchingServer?: boolean;
  hiddenColumns: Set<string>;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
}

interface EditingCell {
  translationId: number;
  languageCode: string;
  originalValue: string;
}

interface EditingDescription {
  translationId: number;
  originalValue: string;
}

type SortDirection = 'asc' | 'desc' | null;

interface SortConfig {
  column: string; // 'keyName', 'description', or language code
  direction: SortDirection;
}

export function TranslationGrid({
  translations,
  languages,
  isLoading,
  error,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onEdit,
  onDelete,
  onUpdateCell,
  onUpdateDescription,
  onRetry,
  isDeleting = false,
  isSearchingServer = false,
  hiddenColumns,
  pageSizeOptions,
  onPageSizeChange,
}: TranslationGridProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [translationToDelete, setTranslationToDelete] = useState<TranslationKey | null>(null);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSavingCell, setIsSavingCell] = useState(false);
  const [editingDescription, setEditingDescription] = useState<EditingDescription | null>(null);
  const [descriptionValue, setDescriptionValue] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancellingDescription, setIsCancellingDescription] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: '', direction: null });

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? currentPage * pageSize + 1 : 0;
  const endItem = Math.min((currentPage + 1) * pageSize, totalCount);

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 4;
    let startPage = Math.max(0, currentPage - 1);
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  // Sort languages for display (English first, then by sortOrder)
  const sortedLanguages = [...languages]
    .sort((a, b) => {
      if (a.languageCode === 'en') return -1;
      if (b.languageCode === 'en') return 1;
      return a.sortOrder - b.sortOrder;
    });

  // Filter out hidden columns (English is always shown)
  const displayLanguages = sortedLanguages.filter(lang =>
    lang.languageCode === 'en' || !hiddenColumns.has(lang.languageCode)
  );

  // Check if columns are hidden
  const isDescriptionHidden = hiddenColumns.has('description');
  const isKeyNameHidden = hiddenColumns.has('keyName');

  const getTranslationValue = (translation: TranslationKey, languageCode: string) => {
    const value = translation.values.find((v) => v.languageCode === languageCode);
    return value?.value || '';
  };

  // Handle column sort
  const handleSort = useCallback((column: string) => {
    setSortConfig(prev => {
      if (prev.column !== column) {
        return { column, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { column, direction: 'desc' };
      }
      if (prev.direction === 'desc') {
        return { column: '', direction: null };
      }
      return { column, direction: 'asc' };
    });
  }, []);

  // Sort translations
  const sortedTranslations = [...translations].sort((a, b) => {
    if (!sortConfig.column || !sortConfig.direction) return 0;

    let aValue: string;
    let bValue: string;

    if (sortConfig.column === 'keyName') {
      aValue = a.keyName.toLowerCase();
      bValue = b.keyName.toLowerCase();
    } else if (sortConfig.column === 'description') {
      aValue = (a.description || '').toLowerCase();
      bValue = (b.description || '').toLowerCase();
    } else {
      // Language column
      aValue = getTranslationValue(a, sortConfig.column).toLowerCase();
      bValue = getTranslationValue(b, sortConfig.column).toLowerCase();
    }

    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Get sort icon for column header (stacked chevrons)
  const getSortIcon = (column: string) => (
    <div className="flex flex-col items-center ml-1">
      <ChevronUp className={cn(
        'h-3 w-3',
        sortConfig.column === column && sortConfig.direction === 'asc'
          ? GRID_STYLES.sort.active
          : GRID_STYLES.sort.inactive
      )} />
      <ChevronDown className={cn(
        'h-3 w-3 -mt-1',
        sortConfig.column === column && sortConfig.direction === 'desc'
          ? GRID_STYLES.sort.active
          : GRID_STYLES.sort.inactive
      )} />
    </div>
  );

  const handleDeleteClick = (translation: TranslationKey) => {
    setTranslationToDelete(translation);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (translationToDelete) {
      onDelete(translationToDelete.translationKeyId);
      setDeleteDialogOpen(false);
      setTranslationToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTranslationToDelete(null);
  };

  const handleCellClick = useCallback((translation: TranslationKey, languageCode: string) => {
    const currentValue = getTranslationValue(translation, languageCode);
    setEditingCell({
      translationId: translation.translationKeyId,
      languageCode,
      originalValue: currentValue,
    });
    setEditValue(currentValue);
  }, []);

  const handleBlur = useCallback(async () => {
    // Guard: If user pressed Escape, don't auto-save
    if (isCancelling) {
      setIsCancelling(false); // Reset for next edit
      return;
    }

    // Guard: No active cell editing
    if (!editingCell) return;

    // Guard: Value unchanged - just close without saving
    if (editValue === editingCell.originalValue) {
      setEditingCell(null);
      setEditValue('');
      return;
    }

    // Perform auto-save
    setIsSavingCell(true);
    try {
      await onUpdateCell(editingCell.translationId, editingCell.languageCode, editValue);
      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Keep editing state on error so user can retry or cancel
    } finally {
      setIsSavingCell(false);
    }
  }, [isCancelling, editingCell, editValue, onUpdateCell]);

  const handleCellCancel = useCallback(() => {
    setIsCancelling(true); // Signal that we're cancelling, not blurring
    setEditingCell(null);
    setEditValue('');
  }, []);

  const handleCellSave = useCallback(async () => {
    if (!editingCell) return;

    // Don't save if value hasn't changed
    if (editValue === editingCell.originalValue) {
      handleCellCancel();
      return;
    }

    setIsSavingCell(true);
    try {
      await onUpdateCell(editingCell.translationId, editingCell.languageCode, editValue);
      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Failed to save cell:', error);
      // Keep editing state on error so user can retry
    } finally {
      setIsSavingCell(false);
    }
  }, [editingCell, editValue, onUpdateCell, handleCellCancel]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCellSave();
    } else if (e.key === 'Escape') {
      e.preventDefault(); // Prevent blur from firing after Escape
      handleCellCancel();
    }
  }, [handleCellSave, handleCellCancel]);

  // Description editing handlers
  const handleDescriptionClick = useCallback((translation: TranslationKey) => {
    setEditingDescription({
      translationId: translation.translationKeyId,
      originalValue: translation.description || '',
    });
    setDescriptionValue(translation.description || '');
  }, []);

  const handleDescriptionBlur = useCallback(async () => {
    // Guard: If user pressed Escape, don't auto-save
    if (isCancellingDescription) {
      setIsCancellingDescription(false); // Reset for next edit
      return;
    }

    // Guard: No active description editing
    if (!editingDescription) return;

    // Guard: Value unchanged - just close without saving
    if (descriptionValue === editingDescription.originalValue) {
      setEditingDescription(null);
      setDescriptionValue('');
      return;
    }

    // Perform auto-save
    setIsSavingDescription(true);
    try {
      await onUpdateDescription(editingDescription.translationId, descriptionValue);
      setEditingDescription(null);
      setDescriptionValue('');
    } catch (error) {
      console.error('Auto-save description failed:', error);
      // Keep editing state on error so user can retry or cancel
    } finally {
      setIsSavingDescription(false);
    }
  }, [isCancellingDescription, editingDescription, descriptionValue, onUpdateDescription]);

  const handleDescriptionCancel = useCallback(() => {
    setIsCancellingDescription(true); // Signal that we're cancelling, not blurring
    setEditingDescription(null);
    setDescriptionValue('');
  }, []);

  const handleDescriptionSave = useCallback(async () => {
    if (!editingDescription) return;

    // Don't save if value hasn't changed
    if (descriptionValue === editingDescription.originalValue) {
      handleDescriptionCancel();
      return;
    }

    setIsSavingDescription(true);
    try {
      await onUpdateDescription(editingDescription.translationId, descriptionValue);
      setEditingDescription(null);
      setDescriptionValue('');
    } catch (error) {
      console.error('Failed to save description:', error);
      // Keep editing state on error so user can retry
    } finally {
      setIsSavingDescription(false);
    }
  }, [editingDescription, descriptionValue, onUpdateDescription, handleDescriptionCancel]);

  const handleDescriptionKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleDescriptionSave();
    } else if (e.key === 'Escape') {
      e.preventDefault(); // Prevent blur from firing after Escape
      handleDescriptionCancel();
    }
  }, [handleDescriptionSave, handleDescriptionCancel]);

  if (isLoading) {
    return <TranslationGridSkeleton languageCount={displayLanguages.length || 5} />;
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (translations.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {isSearchingServer ? (
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Searching server...</span>
          </div>
        ) : (
          <>
            <p>No translations found.</p>
            <p className="text-sm mt-1">Try adjusting your search or add a new translation.</p>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full min-h-0 overflow-hidden">
        {/* Table container - fills available space, scrolls both horizontally and vertically */}
        <div className={cn(
          "border-x border-t rounded-t-lg bg-white flex-1 min-h-0 overflow-auto",
          GRID_STYLES.border
        )}>
          {/* Using raw table for sticky header support */}
          <table className="w-full min-w-max table-auto caption-bottom text-sm">
            <thead className={cn("sticky top-0 z-10", GRID_STYLES.header.background, "[&_tr]:border-b")}>
              <tr className={cn(GRID_STYLES.header.background, "border-b transition-colors")}>
                {!isKeyNameHidden && (
                  <th
                    className={cn(
                      "h-12 px-4 text-left align-middle w-[200px] border-b cursor-pointer transition-colors select-none whitespace-nowrap",
                      GRID_STYLES.border,
                      GRID_STYLES.header.text,
                      GRID_STYLES.header.hoverText,
                      "text-xs font-medium"
                    )}
                    onClick={() => handleSort('keyName')}
                  >
                    <div className="flex items-center">
                      Key Name
                      {getSortIcon('keyName')}
                    </div>
                  </th>
                )}
                {!isDescriptionHidden && (
                  <th
                    className={cn(
                      "h-12 px-4 text-left align-middle w-[200px] border-b cursor-pointer transition-colors select-none whitespace-nowrap",
                      GRID_STYLES.border,
                      GRID_STYLES.header.text,
                      GRID_STYLES.header.hoverText,
                      "text-xs font-medium"
                    )}
                    onClick={() => handleSort('description')}
                  >
                    <div className="flex items-center">
                      Description
                      {getSortIcon('description')}
                    </div>
                  </th>
                )}
                {displayLanguages.map((lang) => (
                  <th
                    key={lang.languageCode}
                    className={cn(
                      "h-12 px-4 text-left align-middle min-w-[150px] border-b cursor-pointer transition-colors select-none whitespace-nowrap",
                      GRID_STYLES.border,
                      GRID_STYLES.header.text,
                      GRID_STYLES.header.hoverText,
                      "text-xs font-medium"
                    )}
                    onClick={() => handleSort(lang.languageCode)}
                  >
                    <div className="flex items-center">
                      {lang.languageName} ({lang.languageCode.toUpperCase()})
                      {getSortIcon(lang.languageCode)}
                    </div>
                  </th>
                ))}
                <th className={cn(
                  "h-12 px-4 text-right align-middle w-[120px] border-b whitespace-nowrap",
                  GRID_STYLES.border,
                  GRID_STYLES.header.text,
                  "text-xs font-medium"
                )}>Actions</th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {sortedTranslations.map((translation) => {
                const isEditingDesc = editingDescription?.translationId === translation.translationKeyId;

                return (
                <tr key={translation.translationKeyId} className={cn(GRID_STYLES.row.default, GRID_STYLES.row.hover, "transition-colors border-b", GRID_STYLES.border)}>
                  {!isKeyNameHidden && (
                    <td className={cn("p-4 align-middle font-mono text-sm", "bg-[#FAFAFA]")}>
                      {translation.keyName}
                    </td>
                  )}
                  {!isDescriptionHidden && (
                    <td
                      className={cn(
                        "text-sm p-0 align-middle",
                        !isEditingDesc && "cursor-pointer hover:bg-blue-50 transition-colors"
                      )}
                      onClick={() => !isEditingDesc && handleDescriptionClick(translation)}
                    >
                      {isEditingDesc ? (
                        <div className="flex items-center gap-1 p-1">
                          <Input
                            value={descriptionValue}
                            onChange={(e) => setDescriptionValue(e.target.value)}
                            onKeyDown={handleDescriptionKeyDown}
                            onBlur={handleDescriptionBlur}
                            autoFocus
                            disabled={isSavingDescription}
                            className="h-8 text-sm"
                            placeholder="Add description..."
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDescriptionSave();
                            }}
                            disabled={isSavingDescription}
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            {isSavingDescription ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDescriptionCancel();
                            }}
                            disabled={isSavingDescription}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className={cn(
                          "px-4 py-2 min-h-[40px] max-w-[250px]",
                          !translation.description && "text-muted-foreground italic"
                        )}>
                          {translation.description || EMPTY_CELL_VALUE}
                        </div>
                      )}
                    </td>
                  )}
                  {displayLanguages.map((lang) => {
                    const value = getTranslationValue(translation, lang.languageCode);
                    const isEditing = editingCell?.translationId === translation.translationKeyId &&
                                     editingCell?.languageCode === lang.languageCode;

                    return (
                      <td
                        key={lang.languageCode}
                        className={cn(
                          "text-sm p-0 align-middle",
                          !isEditing && "cursor-pointer hover:bg-blue-50 transition-colors"
                        )}
                        dir={lang.isRtl ? 'rtl' : 'ltr'}
                        onClick={() => !isEditing && handleCellClick(translation, lang.languageCode)}
                      >
                        {isEditing ? (
                          <div className="flex items-center gap-1 p-1">
                            <Input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={handleKeyDown}
                              onBlur={handleBlur}
                              autoFocus
                              disabled={isSavingCell}
                              className="h-8 text-sm"
                              dir={lang.isRtl ? 'rtl' : 'ltr'}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCellSave();
                              }}
                              disabled={isSavingCell}
                              className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              {isSavingCell ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCellCancel();
                              }}
                              disabled={isSavingCell}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className={cn(
                            "px-4 py-2 min-h-[40px] max-w-[300px]",
                            !value && "text-muted-foreground italic"
                          )}>
                            {value || EMPTY_CELL_VALUE}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="p-4 align-middle text-right">
                    <div className="flex justify-end gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Info className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-xs">
                            <div className="space-y-1 text-xs">
                              <p><span className="font-medium">Created:</span> {formatDateTime(translation.created)}</p>
                              {translation.createdByName && (
                                <p><span className="font-medium">Created by:</span> {translation.createdByName}</p>
                              )}
                              <p><span className="font-medium">Last updated:</span> {formatDateTime(translation.updated)}</p>
                              {translation.updatedByName && (
                                <p><span className="font-medium">Updated by:</span> {translation.updatedByName}</p>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(translation)}
                        title="Edit all translations"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(translation)}
                        title="Delete translation"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer - Always visible at bottom */}
        <div className={cn('flex-shrink-0 flex items-center justify-between px-2 py-3 border-x border-b bg-white', GRID_STYLES.border)}>
          {/* Page Navigation */}
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(0)} disabled={currentPage === 0}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getPageNumbers().map((pageNum) => (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? 'default' : 'ghost'}
                size="sm"
                className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-[#FFCD00] hover:bg-[#E6B800] text-[#262626]' : 'text-[#262626] hover:bg-[#FFFBEB]'}`}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum + 1}
              </Button>
            ))}
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#262626] hover:bg-[#FFFBEB]" onClick={() => onPageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
            <span className="text-sm text-[#606060] ml-2">{currentPage + 1} of {totalPages} pages</span>
          </div>
          {/* Right side */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#606060]">{startItem}-{endItem} of {totalCount.toLocaleString()}</span>
            {pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#606060]">Rows per page:</span>
                <Select value={pageSize.toString()} onValueChange={(value) => onPageSizeChange(parseInt(value, 10))}>
                  <SelectTrigger className="h-8 w-[70px] border-[#E8E8E8] text-[#262626]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pageSizeOptions.map((size) => (
                      <SelectItem key={size} value={size.toString()} className="hover:bg-[#FFFBEB]">{size}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Translation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the translation key{' '}
              <span className="font-mono font-semibold">{translationToDelete?.keyName}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel} disabled={isDeleting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function TranslationGridSkeleton({ languageCount }: { languageCount: number }) {
  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className={cn("border-x border-t rounded-t-lg bg-white flex-1 min-h-0 overflow-auto", GRID_STYLES.border)}>
        <table className="w-full min-w-max table-auto caption-bottom text-sm">
          <thead className={cn("sticky top-0 z-10", GRID_STYLES.header.background)}>
            <tr className={cn(GRID_STYLES.header.background, "border-b", GRID_STYLES.border)}>
              <th className={cn("h-12 px-4 text-left align-middle w-[200px]", GRID_STYLES.header.text, "text-xs font-medium")}>Key Name</th>
              <th className={cn("h-12 px-4 text-left align-middle w-[200px]", GRID_STYLES.header.text, "text-xs font-medium")}>Description</th>
              {Array.from({ length: languageCount }).map((_, i) => (
                <th key={i} className="h-12 px-4 text-left align-middle min-w-[150px]">
                  <Skeleton className="h-4 w-20" />
                </th>
              ))}
              <th className={cn("h-12 px-4 text-right align-middle w-[100px]", GRID_STYLES.header.text, "text-xs font-medium")}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 10 }).map((_, rowIndex) => (
              <tr key={rowIndex} className={cn(GRID_STYLES.row.default, GRID_STYLES.row.hover, "border-b", GRID_STYLES.border)}>
                <td className="p-4 align-middle">
                  <Skeleton className="h-4 w-32" />
                </td>
                <td className="p-4 align-middle">
                  <Skeleton className="h-4 w-28" />
                </td>
                {Array.from({ length: languageCount }).map((_, colIndex) => (
                  <td key={colIndex} className="p-4 align-middle">
                    <Skeleton className="h-4 w-24" />
                  </td>
                ))}
                <td className="p-4 align-middle">
                  <Skeleton className="h-8 w-16" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
